import { useState } from 'react'
import { Box, ButtonBase } from '@mui/material'
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight'
import type { ReactNode } from 'react'
import { colorTokens } from '../../../shared/styles/theme'
import { RADIUS } from '../../../shared/styles/radii'

/** 顧客側とキーを分ける。同じ端末で両方の面を使うことがある。 */
const OPEN_KEY = 'mamoru.annotator.sidebar.open'

/** 開閉は端末に残す。localStorage が使えない環境でも開いた状態で成立させる。 */
const readIsOpen = () => {
  try {
    return window.localStorage.getItem(OPEN_KEY) !== 'false'
  } catch {
    return true
  }
}

const writeIsOpen = (isOpen: boolean) => {
  try {
    window.localStorage.setItem(OPEN_KEY, String(isOpen))
  } catch {
    // 保存できなくても開閉そのものは成立する
  }
}

interface Props {
  sidebar: (props: { onCollapse: () => void }) => ReactNode
  children: ReactNode
}

/**
 * annotator 閲覧面の app shell: Sidebar(224px・格納可能) + Main。
 * 顧客側 apps/medilab/latest/Layout と同じ作法で、**TopBar を置かない**
 * (04-apps/mamoru.md 判断2 / 00-principles/03-subtraction.md §1)。
 *
 * 格納中の復帰導線 `>>` は Main の左上に置く(顧客側と同じ逸脱)。
 *
 * Sidebar は render prop で受ける。顧客側は Sidebar が自分でデータを引くのに対し、
 * こちらは薬局・一覧・リクエストID を App が持つため(状態の置き場が違うだけで作法は同じ)。
 */
export const Layout = ({ sidebar, children }: Props) => {
  const [isOpen, setIsOpen] = useState(readIsOpen)

  const setOpen = (next: boolean) => {
    setIsOpen(next)
    writeIsOpen(next)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100vh',
        background: colorTokens.bg.default,
      }}
    >
      {isOpen && sidebar({ onCollapse: () => setOpen(false) })}
      <Box
        component="main"
        sx={{ flex: 1, minWidth: 0, position: 'relative', overflow: 'hidden' }}
      >
        {!isOpen && (
          <ButtonBase
            aria-label="一覧を開く"
            onClick={() => setOpen(true)}
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              zIndex: 1,
              width: 24,
              height: 24,
              borderRadius: `${RADIUS.md}px`,
              color: colorTokens.fg.muted,
              background: colorTokens.bg.default,
              border: `1px solid ${colorTokens.border.default}`,
              boxShadow: colorTokens.shadow.cardSubtle,
            }}
          >
            <KeyboardDoubleArrowRightIcon sx={{ fontSize: 14 }} />
          </ButtonBase>
        )}
        {children}
      </Box>
    </Box>
  )
}
